/**
 * Staff Management Tab
 * Allows hotel owners/managers to create and manage staff accounts
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Users, 
  Plus, 
  Trash2, 
  Shield,
  CheckCircle,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';


export function StaffManagementTab({ hotelId }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'housekeeping',
    password: ''
  });

  useEffect(() => {
    loadStaff();
  }, [hotelId]);

  const loadStaff = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/staff`);
      const data = await response.json();
      
      if (data.success) {
        setStaff(data.staff || []);
      }
    } catch (error) {
      console.error('Failed to load staff:', error);
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const createStaffMember = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/staff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          hotel_id: hotelId
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`Staff member created: ${formData.name}`);
        setShowCreateForm(false);
        setFormData({ name: '', email: '', role: 'housekeeping', password: '' });
        loadStaff();
      } else {
        toast.error(data.error || 'Failed to create staff member');
      }
    } catch (error) {
      console.error('Failed to create staff:', error);
      toast.error('Failed to create staff member');
    }
  };

  const deleteStaffMember = async (staffId) => {
    if (!confirm('Are you sure you want to delete this staff member?')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/staff/${staffId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Staff member deleted');
        loadStaff();
      }
    } catch (error) {
      console.error('Failed to delete staff:', error);
      toast.error('Failed to delete staff member');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'housekeeping':
        return 'bg-[hsl(var(--status-clean))]/10 text-[hsl(var(--status-clean))] border-[hsl(var(--status-clean))]/20';
      case 'maintenance':
        return 'bg-[hsl(38_92%_55%)]/10 text-[hsl(38_92%_55%)] border-[hsl(38_92%_55%)]/20';
      case 'front_desk':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'manager':
        return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
      default:
        return '';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'housekeeping':
        return '🧹';
      case 'maintenance':
        return '🔧';
      case 'front_desk':
        return '🏨';
      case 'manager':
        return '👔';
      default:
        return '👤';
    }
  };

  // Group staff by role
  // Handle two formats: 
  // 1. role directly (e.g., role="housekeeping") from staff_router
  // 2. role="staff" + staff_type="housekeeping" from enhanced_staff_router
  const getStaffRole = (member) => {
    if (member.role === 'manager') return 'manager';
    if (member.role === 'staff' && member.staff_type) return member.staff_type;
    return member.role; // Direct role assignment
  };

  const staffByRole = {
    housekeeping: staff.filter(s => getStaffRole(s) === 'housekeeping'),
    maintenance: staff.filter(s => getStaffRole(s) === 'maintenance'),
    front_desk: staff.filter(s => getStaffRole(s) === 'front_desk'),
    manager: staff.filter(s => getStaffRole(s) === 'manager')
  };

  return (
    <div className="space-y-6" data-testid="staff-management-tab">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl heading-font tracking-tight flex items-center gap-2">
            <Users className="w-6 h-6" />
            Staff Management
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Create accounts for housekeeping, maintenance, and front desk staff
          </p>
        </div>
        
        <Button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          data-testid="create-staff-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Staff
        </Button>
      </div>

      {/* Info Alert */}
      <Alert className="border-primary/20 bg-primary/5">
        <Sparkles className="w-4 h-4 text-primary" />
        <AlertDescription className="text-sm">
          <strong>Dashboard Access:</strong> Staff members can log in with their email and password to access their department dashboard:
          <ul className="list-disc ml-5 mt-2 space-y-1">
            <li>Housekeeping → <code className="text-xs bg-background px-1 py-0.5 rounded">/housekeeping</code></li>
            <li>Maintenance → <code className="text-xs bg-background px-1 py-0.5 rounded">/maintenance</code></li>
            <li>Front Desk → <code className="text-xs bg-background px-1 py-0.5 rounded">/checkin</code></li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Create Form */}
      {showCreateForm && (
        <Card className="border-primary/30" data-testid="create-staff-form">
          <CardHeader>
            <CardTitle className="text-lg">Create New Staff Member</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={createStaffMember} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="staff-name">Full Name *</Label>
                  <Input
                    id="staff-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Maria Garcia"
                    required
                    data-testid="staff-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="staff-email">Email *</Label>
                  <Input
                    id="staff-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="maria@hotel.com"
                    required
                    data-testid="staff-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="staff-role">Role *</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="staff-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="housekeeping">🧹 Housekeeping</SelectItem>
                      <SelectItem value="maintenance">🔧 Maintenance</SelectItem>
                      <SelectItem value="front_desk">🏨 Front Desk</SelectItem>
                      <SelectItem value="manager">👔 Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="staff-password">Password *</Label>
                  <Input
                    id="staff-password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                    data-testid="staff-password-input"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Button type="submit" data-testid="submit-create-staff">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Create Staff Account
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowCreateForm(false)}
                  data-testid="cancel-create-staff"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Staff by Role */}
      <div className="space-y-6">
        {Object.entries(staffByRole).map(([role, members]) => (
          members.length > 0 && (
            <div key={role}>
              <h4 className="text-lg heading-font mb-3 flex items-center gap-2 capitalize">
                <span className="text-xl">{getRoleIcon(role)}</span>
                {role.replace('_', ' ')} Staff ({members.length})
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {members.map((member) => (
                  <Card key={member.id} data-testid={`staff-card-${member.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base">{member.name}</CardTitle>
                          <p className="text-xs text-muted-foreground mt-1">{member.email}</p>
                        </div>
                        <Badge className={getRoleBadgeColor(member.role)}>
                          {member.role.replace('_', ' ')}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">
                          Created: {new Date(member.created_at).toLocaleDateString()}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => deleteStaffMember(member.id)}
                          data-testid={`delete-staff-${member.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )
        ))}
      </div>

      {/* Empty State */}
      {staff.length === 0 && !showCreateForm && (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="heading-font text-lg mb-2">No Staff Members</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create staff accounts to give your team access to their dashboards
            </p>
            <Button onClick={() => setShowCreateForm(true)} data-testid="empty-create-staff-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create First Staff Member
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Staff Login Instructions */}
      {staff.length > 0 && (
        <Card className="border-muted">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Staff Login Instructions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>Staff members should:</p>
            <ol className="list-decimal ml-5 space-y-1">
              <li>Go to <code className="bg-muted px-1 py-0.5 rounded text-xs">/login</code></li>
              <li>Enter their email and password</li>
              <li>They'll be automatically redirected to their department dashboard</li>
            </ol>
            <p className="pt-2 text-xs">
              💡 <strong>Tip:</strong> Managers can access all dashboards. Housekeeping/Maintenance staff can only access their department.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
